import { Button } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Badge, Show } from '@side/pantry';
import { kebabCase } from 'lodash';
import type { ReactNode } from 'react';

export const CARD_DIMENSION_IN_PIXELS = 220;

type NeedsAttentionCardProps = {
  itemCount: number;
  color: 'error' | 'warning';
  headerEndAdornment?: ReactNode | undefined;
  headerStartAdornment: ReactNode;
  onClick: () => void;
  supplementaryDetails: string[];
  title: string;
};

export function NeedsAttentionCard(props: NeedsAttentionCardProps) {
  const {
    itemCount,
    color,
    headerEndAdornment,
    headerStartAdornment,
    onClick,
    supplementaryDetails,
    title,
  } = props;

  const areAttentionItemsPresent = Boolean(itemCount);

  if (!areAttentionItemsPresent) return null;

  return (
    <Card
      id={`${kebabCase(title)}-card`}
      aria-label={title}
      component={Button}
      focusRipple={false}
      onClick={onClick}
      sx={{
        textAlign: 'unset',
        borderColor: 'other.outlineBorder',
        borderRadius: 2,
        borderWidth: 2,
        height: CARD_DIMENSION_IN_PIXELS,
        padding: 0,
        scrollSnapAlign: 'start',
        width: CARD_DIMENSION_IN_PIXELS,
        '&.Mui-focusVisible': {
          borderColor: 'other.spaceCadet',
        },
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          width: '100%',
          '&:last-child': {
            paddingBottom: 2,
          },
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', gap: 1, marginBottom: 2 }}
        >
          {headerStartAdornment}

          <Show when={headerEndAdornment}>
            <Stack sx={{ alignItems: 'center' }} direction="row">
              {headerEndAdornment}
            </Stack>
          </Show>
        </Stack>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            marginBottom: 2,
            alignItems: 'flex-start',
          }}
        >
          <Typography variant="h5">{title}</Typography>

          <Badge
            badgeContent={itemCount}
            color={color}
            data-testid="NeedsAttentionCard__Badge"
            fill="outlined"
            variant="inline"
          />
        </Stack>

        <Divider
          sx={{
            borderBottomWidth: 2,
            borderColor: `${color}.main`,
            marginBottom: 2,
            width: 24,
          }}
        />

        {supplementaryDetails.map((supplementaryDetail) => (
          <Stack
            direction="row"
            key={supplementaryDetail}
            sx={{ marginBottom: 1 }}
          >
            <Typography variant="body3">{supplementaryDetail}</Typography>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

NeedsAttentionCard.displayName = 'NeedsAttentionCard';
